import dayjs from 'dayjs';
import { z } from 'zod';
import {
  ELanguage,
  EOrganizationProvisioningScope,
  EOrganizationProvisioningType,
  TNullable,
} from '@/types';
import {
  emailSchemaRequired,
  generalTextSchema,
  nameSchema,
  phoneSchema,
} from '@/utils/schemasZod';
import { getDateOnlyISOFormat } from '@/utils/convertDate';

/**
 * One contract period of the provisioning form. `id` is set only on contracts
 * loaded from the API; a contract without one is sent as a new contract.
 */
const contractSchema = z.object({
  id: z.string().optional(),
  // Counts are checked only where the provisioning type and scope show them.
  seats: z.string().nullable().optional(),
  stationLicenses: z.string().nullable().optional(),
  consultsPerMonth: z.string().nullable().optional(),
  contractStartDate: z.date().nullable().optional(),
  contractEndDate: z.date().nullable().optional(),
});

/**
 * Whether the contract start/end dates are required (and shown) for the
 * given provisioning type/scope combination.
 * @param provisioning - The provisioning type and scope to check.
 * @param provisioning.type - The provisioning type.
 * @param provisioning.scope - The provisioning scope.
 * @returns `true` if the contract period is required for this combination.
 */
export const isContractPeriodRequired = (provisioning: {
  type?: EOrganizationProvisioningType;
  scope?: TNullable<string>;
}) =>
  provisioning.type === EOrganizationProvisioningType.CONSULTS_PER_MONTH ||
  (provisioning.type === EOrganizationProvisioningType.SEATS &&
    provisioning.scope === EOrganizationProvisioningScope.ORGANIZATION);

/**
 * Zod schema for the create organization form, validated across all tabs.
 */
export const createOrganizationSchema =
  /**
   * Zod schema for the create organization form, validated across all tabs.
   */
  z.object({
    name: generalTextSchema
      .min(1, { message: 'zod.field-required' })
      .refine((val) => /\p{L}|\p{N}/u.test(val), {
        message: 'zod.name-must-contain-alphanumeric',
      }),
    language: z.nativeEnum(ELanguage),
    country: generalTextSchema.nullable().optional(),
    state: generalTextSchema.nullable().optional(),
    address1: z.string().nullable().optional(),
    address2: z.string().nullable().optional(),
    city: generalTextSchema.nullable().optional(),
    zip: generalTextSchema.nullable().optional(),
    pocSettings: z.object({
      name: nameSchema.min(1, { message: 'zod.field-required' }),
      title: generalTextSchema.min(1, { message: 'zod.field-required' }),
      email: emailSchemaRequired,
      phone: phoneSchema.nullable().optional(),
      country: generalTextSchema.nullable().optional(),
      state: generalTextSchema.nullable().optional(),
      address1: z.string().nullable().optional(),
      address2: z.string().nullable().optional(),
      zip: generalTextSchema.nullable().optional(),
    }),
    configuration: z
      .object({
        tfa: z.boolean(),
        symptomTriage: z.boolean(),
        triageProvider: z.enum(['infermedica', 'isabel']).optional(),
        medicalStationCalls: z.boolean(),
        canarySpeech: z.boolean(),
        medwand: z.boolean(),
      })
      .refine((data) => !data.symptomTriage || !!data.triageProvider, {
        path: ['triageProvider'],
        message: 'zod.field-required',
      }),
    provisioning: z
      .object({
        type: z.nativeEnum(EOrganizationProvisioningType),
        scope: z.string().optional().nullable(),
        contracts: z.array(contractSchema),
      })
      .superRefine((data, ctx) => {
        const addIssue = (path: (string | number)[], message: string) =>
          ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });

        if (data.type === EOrganizationProvisioningType.SEATS && !data.scope) {
          addIssue(['scope'], 'zod.field-required');
        }

        const isConsults =
          data.type === EOrganizationProvisioningType.CONSULTS_PER_MONTH;

        // Contracts that are not shown are neither checked nor sent.
        if (!isContractPeriodRequired(data)) return;

        const counts = isConsults
          ? (['consultsPerMonth'] as const)
          : (['seats', 'stationLicenses'] as const);

        data.contracts.forEach((contract, index) => {
          const requireField = (field: keyof typeof contract) => {
            if (!contract[field]) {
              addIssue(['contracts', index, field], 'zod.field-required');
            }
          };

          counts.forEach((field) => {
            requireField(field);
            if (contract[field] && !Number(contract[field])) {
              addIssue(
                ['contracts', index, field],
                'zod.must-be-greater-than-zero',
              );
            }
          });
          requireField('contractStartDate');
        });

        const today = getDateOnlyISOFormat(dayjs().utc());
        const activeContract = data.contracts.find(
          ({ id, contractStartDate, contractEndDate }) =>
            !!id &&
            !!contractStartDate &&
            !!contractEndDate &&
            dayjs(today).isBetween(
              contractStartDate,
              contractEndDate,
              'day',
              '[]',
            ),
        );
        data.contracts.forEach(({ id, contractStartDate }, index) => {
          if (
            id ||
            !activeContract?.contractEndDate ||
            !contractStartDate ||
            dayjs(contractStartDate).isAfter(
              activeContract.contractEndDate,
              'day',
            )
          ) {
            return;
          }
          addIssue(
            ['contracts', index, 'contractStartDate'],
            'zod.contract-start-date-before-active-end-date',
          );
        });

        // Contracts never overlap: ordered by start, as the API orders them,
        // each one starts after the one before it ends. A new contract may
        // still go before a saved one that has not started, so their order in
        // the list is not used.
        const byStart = data.contracts
          .map((contract, index) => ({ ...contract, index }))
          .filter(({ contractStartDate }) => !!contractStartDate)
          .sort((a, b) => dayjs(a.contractStartDate).diff(b.contractStartDate));
        byStart.slice(1).forEach((contract, i) => {
          const previous = byStart[i];
          if (
            !previous.contractEndDate ||
            !contract.contractStartDate ||
            dayjs(contract.contractStartDate).isAfter(
              previous.contractEndDate,
              'day',
            )
          ) {
            return;
          }
          addIssue(
            ['contracts', contract.index, 'contractStartDate'],
            'zod.contract-start-date-overlaps-previous',
          );
        });
      }),
  });

export type TCreateOrganizationForm = z.infer<typeof createOrganizationSchema>;
export type TOrganizationContractForm = z.infer<typeof contractSchema>;

/**
 * Zod schema for the form as an Org Admin edits it: configuration and
 * provisioning are read-only for them and never sent, so they are passed
 * through unchecked, and saved values the full schema would reject do not
 * block a change elsewhere.
 */
export const orgAdminOrganizationSchema =
  /**
   * Zod schema for the form as an Org Admin edits it, provisioning and
   * configuration unchecked.
   */
  createOrganizationSchema.extend({
    configuration: z.custom<TCreateOrganizationForm['configuration']>(),
    provisioning: z.custom<TCreateOrganizationForm['provisioning']>(),
  });
